import { useState, useCallback, useEffect, useRef } from 'react';

import { useApi } from '@paradigm/api';
import { ONE_CLICK_TRADING, track } from '@paradigm/analytics/src/analytics';

import { submitQuote, CreateQuoteReport } from '#/unified-rfqs/ui/submit-quote';
import {
  getOppositeSide,
  parseClearingUnitiesToQty,
} from '#/unified-rfqs/ui/rfqs/quotes-functions';
import { GrfqNewQuoteLeg } from '#/unified-rfqs/repositories/grfq-types';
import { Grfq, GrfqLeg, GrfqQuote } from '#/unified-rfqs/entities/grfq/domain';

import { useNotificationHandlerActions } from '#/unified-rfqs/ui/notifications/notifications-handler-context';
import {
  Status,
  SubmitCallback,
} from '#/unified-rfqs/ui/one-click-trade/types';
import { createFailedResp } from '#/unified-rfqs/ui/one-click-trade/fetch-functions';
import { useGrfqService } from '#/unified-rfqs/contexts/grfqs';
import { useDefaultAccountsActions } from '#/preferences/default-accounts/context';
import { logException } from '@paradigm/logging/src/logging';

interface Args {
  quantity: string;
  quote: GrfqQuote | undefined;
  rfq: Grfq;
}

function createLegs(rfq: Grfq, quote: GrfqQuote): GrfqNewQuoteLeg[] {
  if (rfq.legs.length !== quote.legs.length) {
    return [];
  }

  return rfq.legs.map((leg: GrfqLeg, index) => ({
    instrument: leg.instrument,
    price: quote.legs[index]!.price ?? '',
  }));
}

const CONFIRM_WINDOW_MS = 3000;

export default function useSubmitAggressQuote({
  quantity,
  quote: topOfBookQuote,
  rfq,
}: Args) {
  const resetStateTimeoutIdRef = useRef<ReturnType<typeof setTimeout>>();
  const grfqService = useGrfqService();
  const [status, setStatus] = useState<Status>('idle');
  const [lockedQuote, setLockedQuote] = useState<GrfqQuote | undefined>();
  const [, createQuote] = useApi(submitQuote(grfqService.createQuote));
  const { getDefaultAccount } = useDefaultAccountsActions();
  const { pauseNotificationsFor, resumeNotifications } =
    useNotificationHandlerActions();

  useEffect(() => {
    return () => clearTimeout(resetStateTimeoutIdRef.current);
  }, []);

  const submitAggressQuote = useCallback(
    async (cb: SubmitCallback<CreateQuoteReport>) => {
      if (topOfBookQuote === undefined) {
        logException(
          'OB Invalid state: Order is null when trying to enter confirm window.',
          null,
        );
        const failedResp = createFailedResp(
          `Failed to cross quote. Please try again.`,
        );

        cb(failedResp);
        return;
      }
      if (status !== 'confirming') {
        setStatus('confirming');
        pauseNotificationsFor(CONFIRM_WINDOW_MS);
        // Lock quote with top of the book quote at the moment of sell/buy click
        setLockedQuote(topOfBookQuote);

        clearTimeout(resetStateTimeoutIdRef.current);
        resetStateTimeoutIdRef.current = setTimeout(() => {
          setLockedQuote(undefined);
          setStatus('idle');
        }, CONFIRM_WINDOW_MS);
        return;
      }

      resumeNotifications();
      if (lockedQuote === undefined) {
        logException(
          'OB Invalid state: Order is null when trying to confirm cross action.',
          null,
        );
        const failedResp = createFailedResp(
          `Failed to cross quote. Please try again.`,
        );

        cb(failedResp);
        return;
      }

      const legs = createLegs(rfq, lockedQuote);
      if (legs.length === 0) {
        cb(
          createFailedResp(
            'Could not create leg prices for quote. Please try again later.',
          ),
        );
        return;
      }

      const productCode = rfq.product_codes[0];
      const defaultAccountName = getDefaultAccount(rfq.venue, productCode);
      if (defaultAccountName == null) {
        const failedResp = createFailedResp(
          `Could not retrieve ${rfq.venue} default account. Please try again.`,
        );

        cb(failedResp);
        return;
      }

      setStatus('submitting');

      const newQuote = {
        quantity: parseClearingUnitiesToQty(quantity, rfq.product_codes[0]!)
          .qty,
        legs,
        side: getOppositeSide(lockedQuote.side),
        account: defaultAccountName,
        ioc: true,
      };

      createQuote({ rfqId: rfq.id, quote: newQuote }, (res) => {
        const event = res.ok
          ? ONE_CLICK_TRADING.QUOTE_SENT_SUCCESS
          : ONE_CLICK_TRADING.QUOTE_SENT_ERROR;

        setLockedQuote(undefined);
        track(event);
        cb(res);
        setStatus('idle');
      });
    },
    [
      topOfBookQuote,
      status,
      resumeNotifications,
      lockedQuote,
      rfq,
      getDefaultAccount,
      quantity,
      createQuote,
      pauseNotificationsFor,
    ],
  );

  const resetConfirmState = useCallback(() => {
    if (status !== 'confirming') return;

    clearTimeout(resetStateTimeoutIdRef.current);
    resetStateTimeoutIdRef.current = undefined;
    setStatus('idle');
    resumeNotifications();
    setLockedQuote(undefined);
  }, [resumeNotifications, status]);

  return {
    resetConfirmState,
    status,
    lockedQuote,
    submitAggressQuote,
  };
}
